import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { candidateAPI } from '../../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const ProcessingStatusPolling = ({ assignmentId, onVideoReady }) => {
  const [status, setStatus] = useState('processing');
  const [recordingId, setRecordingId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let intervalId = null;

    const checkStatus = async () => {
      try {
        const data = await candidateAPI.getTestRecordingStatus(assignmentId);

        if (!data.has_recording) {
          setStatus('no_recording');
          return;
        }

        setStatus(data.processing_status);
        setRecordingId(data.recording_id);

        // If completed or failed, stop polling
        if (data.processing_status === 'completed') {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          if (onVideoReady) {
            onVideoReady(data.recording_id);
          }
        } else if (data.processing_status === 'failed') {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          setError('Video processing failed. Please try recording again.');
        }
      } catch (err) {
        console.error('Error checking status:', err);
        setError(err.message || 'Failed to check processing status');
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    // Initial check
    checkStatus();

    // Poll every 3 seconds if still processing
    intervalId = setInterval(() => {
      if (status === 'processing') {
        checkStatus();
      }
    }, 3000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [assignmentId, status, onVideoReady]);

  if (status === 'no_recording') {
    return null;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (status === 'processing') {
    return (
      <Box sx={{ textAlign: 'center', mt: 3, mb: 3 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Processing your test recording...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This usually takes 10-30 seconds. Please wait while we merge your screen, webcam, and audio.
        </Typography>
      </Box>
    );
  }

  if (status === 'completed' && recordingId) {
    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Test recording processed successfully! Your video is ready.
        </Alert>

        <Typography variant="h6" gutterBottom>
          Your Test Recording:
        </Typography>

        <Box
          sx={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: '#000',
            borderRadius: 1,
          }}
        >
          <video
            controls
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
            src={`${API_BASE_URL}/recordings/${recordingId}/merged`}
          >
            Your browser does not support the video tag.
          </video>
        </Box>
      </Box>
    );
  }

  return null;
};

export default ProcessingStatusPolling;
